import { HTTPError } from "ky";
import type { FieldValues, UseFormSetError } from "react-hook-form";
import type { ErrorEnvelope } from "@/api/types";
import { applyFieldErrors } from "@/lib/apply-field-errors";

export function isErrorEnvelope(data: unknown): data is ErrorEnvelope {
  return (
    typeof data === "object" &&
    data !== null &&
    "errors" in data &&
    Array.isArray((data as Record<string, unknown>).errors)
  );
}

/**
 * Handles form submission errors: reads pre-parsed `error.data` from ky v2's
 * HTTPError (body stream is already consumed by ky), applies 422 field errors
 * to the form, or sets a generic root error for unexpected/network failures.
 *
 * Pass `registeredFields` to ensure errors targeting non-existent form fields
 * still surface as a visible root error instead of being silently swallowed.
 */
export function handleFormError<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fallbackMessage = "Something went wrong. Please try again.",
  registeredFields?: Set<string>,
) {
  if (error instanceof HTTPError && isErrorEnvelope(error.data)) {
    const unhandled = applyFieldErrors<T>(error.data.errors, setError, registeredFields);
    if (unhandled.length > 0) {
      setError("root", { message: unhandled[0]?.message });
    }
    return;
  }

  setError("root", { message: fallbackMessage });
}
